import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useEffect } from 'react';

export interface SystemBanner {
  id: string;
  title: string;
  message: string;
  priority: 'info' | 'warning' | 'urgent';
  is_active: boolean;
  valid_from: string;
  valid_until: string | null;
  created_at: string;
}

export function useSystemBanners() {
  const queryClient = useQueryClient();

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['system-banners'],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('system_banners')
        .select('id, title, message, priority, is_active, valid_from, valid_until, created_at')
        .eq('is_active', true)
        .lte('valid_from', now)
        .or(`valid_until.is.null,valid_until.gt.${now}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as SystemBanner[];
    },
    staleTime: 2 * 60 * 1000,
  });

  // Real-time subscription for banner changes
  useEffect(() => {
    const channel = supabase
      .channel('system-banners-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_banners',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['system-banners'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return { banners, isLoading };
}
